import React, { useCallback } from 'react';
import { routes } from '../../../config';
import { useIntersectionObserver } from '../../../hooks/use-intersection-observer';
import { Layout } from '../../../queries/ats/layout/types';
import { Title } from '../../../queries/ats/titles/types/title';
import { tracker } from '../../../tracker';
import { ItemClickedEvent } from '../../../tracker/events/item-clicked/item-clicked';
import { PromotionSeenEvent } from '../../../tracker/events/promotion-seen/promotion-seen';
import { composeEventHandlers } from '../../../utils/commonUtils';
import { DetailsLink } from '../../../views/ats-details/details-link';
import { DetailsSearchParams } from '../../../views/ats-details/use-details-search-params';
import { InputContext } from '../../../views/ats-details/use-input-context';
import { usePageContext } from '../../../views/ats-pages/page/page-context';
import { useSectionContext } from '../../../views/ats-pages/section/section-context';
import { Link } from '../../Link';
import { AtsItemOverwrites } from '../ats-asset/atsAsset.interfaces';
import { LinkItem } from './LinkItem';

function getTitleParams(
  title: Title,
  page: Layout.PageView,
  section: Layout.SectionView,
  input: Partial<DetailsSearchParams> | undefined,
): Partial<DetailsSearchParams> {
  const params: Partial<DetailsSearchParams> = {
    page: page.slug,
    section: section.slug,
  };

  if (title.orderProvidersBy?.id !== null) {
    params.orderByProviderId = title.orderProvidersBy?.id;
  }

  if (title.orderProvidersBy?.type !== null) {
    params.orderByServiceType = title.orderProvidersBy?.type;
  }

  if (input?.platformTitleId !== undefined) {
    params.platformTitleId = input.platformTitleId;
  }

  if (input?.orderByProviderId !== undefined) {
    params.orderByProviderId = input.orderByProviderId;
  }

  if (input?.orderByServiceType !== undefined) {
    params.orderByServiceType = input.orderByServiceType;
  }

  return params;
}

interface AtsItemSharedProps {
  title?: string;
  className?: string;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
}

interface AtsItemProps extends AtsItemSharedProps {
  children: React.ReactNode;
  item: Layout.SectionItem;
  pattern: Layout.Pattern;
  overwrites?: AtsItemOverwrites;
  params?: Partial<InputContext>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export function AtsItem({ item, overwrites, onClick, pattern, params, ...rest }: AtsItemProps) {
  const page = usePageContext('AtsItem');
  const section = useSectionContext('AtsItem');

  const handleObserved = useCallback(() => {
    if (item.params?.isPromo === 'true') {
      const event = new PromotionSeenEvent(item, section, page, pattern);
      const id = event.properties.referral_id;

      if (!page.hasSeenPromotion(id)) {
        tracker.track(event);
        page.promotionSeen(id);
      }
    }
  }, [item, page, pattern, section]);

  const ref = useIntersectionObserver<HTMLAnchorElement>({
    onObserved: handleObserved,
    triggerOnce: true,
    threshold: 1.0,
  });

  const handleClick = composeEventHandlers(onClick, () => {
    if (item.params?.isPromo === 'true') {
      tracker.track(new ItemClickedEvent(item, section, page, pattern));
    }
  });

  if (item.type === 'image') {
    return <div {...rest} />;
  }

  if (item.type === 'trailer') {
    return (
      <Link
        {...rest}
        to={`${routes.playback}${routes.vod.playTrailer}/${item.id}`}
        onClick={onClick}
        ref={ref}
        {...rest}
      />
    );
  }

  if (item.type === 'page') {
    return <Link {...rest} to={item.slug} onClick={handleClick} ref={ref} {...rest} />;
  }

  if (item.type === 'link') {
    return <LinkItem {...rest} item={item} pattern={pattern} onClick={handleClick} ref={ref} {...rest} />;
  }

  if (item.type === 'title') {
    return (
      <DetailsLink
        {...rest}
        aria-label={`${item.name} - ${item.title.type}`}
        type={item.title.type}
        id={overwrites?.linkId ?? item.title.masterId}
        params={getTitleParams(item.title, page, section, params)}
        onClick={handleClick}
        ref={ref}
      >
        {rest.children}
      </DetailsLink>
    );
  }

  if (item.type === 'unmanaged') {
    switch (item.params.action) {
      case 'streaming-services':
        return (
          <Link {...rest} to={routes.unmanaged.base + routes.svod.base} onClick={handleClick} ref={ref} {...rest} />
        );
      case 'my-recordings':
        return (
          <Link {...rest} to={routes.unmanaged.base + routes.pvr.base} onClick={handleClick} ref={ref} {...rest} />
        );
      case 'watch-tv':
        return (
          <Link {...rest} to={routes.unmanaged.base + routes.watchTv.base} onClick={handleClick} ref={ref} {...rest} />
        );
      case 'tv-guide':
        return (
          <Link {...rest} to={routes.unmanaged.base + routes.tvguide.base} onClick={handleClick} ref={ref} {...rest} />
        );
      default:
        console.warn('Unhandled unmanaged link', item);
        return null;
    }
  }

  console.error('Unhandled link', item);

  return (
    // @ts-expect-error
    <Link {...rest} ref={ref}>
      {rest.children}
    </Link>
  );
}
